import { Database } from './database.types.ts';

// Use this type to prettify the types that are unions of other types or interfaces
// EXAMPLE: type NiceReadableType = PrettifyType<SomeUnionType>
export type PrettifyType<T> = {
  [K in keyof T]: T[K];
} & unknown;

type DbOnboardingSessionPure =
  Database['public']['Tables']['onboarding_session']['Row'];
type DbOnboardingUsers = Database['public']['Views']['OnboardingUsers']['Row'];
type ReviewableBusinessesPure =
  Database['public']['Views']['ReviewableBusinesses']['Row'];
type ReviewFraudDataPure =
  Database['public']['Tables']['reviews_fraud_data']['Row'];
type DbUserPure = Database['public']['Tables']['users']['Row'];
type DbReviewPure = Database['public']['Tables']['reviews']['Row'];
export type OperatorPure = Database['public']['Tables']['operators']['Row'];

export type OnboardingSession = PrettifyType<
  DbOnboardingSessionPure & { stepData: OnboardingSessionStepData }
>;

/**
 * IMPORTANT: OnboardingUsers is a DATABASE VIEW, not a table.
 * You CANNOT update/insert/delete directly on this view.
 * Use the underlying tables (like onboarding_session) for mutations.
 * This view is READ-ONLY and used for querying joined data.
 */
export type OnboardingUsers = PrettifyType<DbOnboardingUsers>;
export type User = PrettifyType<DbUserPure>;
export type Review = PrettifyType<DbReviewPure>;
export type ReviewableBusinesses = PrettifyType<ReviewableBusinessesPure>;
export type ReviewFraudData = PrettifyType<ReviewFraudDataPure>;
export type Operator = PrettifyType<OperatorPure>;

export type RiskPreference = Database['public']['Enums']['operatorSpecialty'];
export type RatingInvestmentAmount =
  Database['public']['Enums']['ratingInvestmentAmount'];
export type Status = Database['public']['Enums']['status'];

export enum ONBOARDING_STATUS {
  ONBOARDING_STARTED = 'onboarding_started',
  ESTIMATE_VIEWED = 'estimateViewed',
  CALL_SCHEDULED = 'call_scheduled',
  CALL_ATTENDED = 'call_attended',
  NOT_INVESTING = 'not_investing',
  CONVERTED = 'converted',
  NOT_VALID = 'not_valid',
}

export enum OPERATOR_SPECIALTY {
  RETURNS = 'returns',
  DEDUCTIONS = 'deductions',
  LIQUIDITY = 'liquidity',
  BALANCED_RETURNS_DEDUCTIONS = 'balanced_returns_deductions',
  LOW_RISK = 'low_risk',
}

export enum FILING_STATUS {
  SINGLE = 'single',
  MARRIED_JOINT = 'married_joint',
  MARRIED_SEPARATE = 'married_separate',
  HEAD_HOUSEHOLD = 'head_household',
}

export enum INCOME_TYPE {
  W2 = 'w2',
  CONTRACT_1099 = '1099',
  BUSINESS = 'business',
  OTHER = 'other',
}

export enum RISK_TOLERANCE {
  CONSERVATIVE = 'conservative',
  GROWTH_ORIENTED = 'growth_oriented',
}
export enum STATUS {
  ACTIVE = 'active',
  PENDING = 'pending',
  REMOVED = 'removed',
  EDITING = 'editing',
}

export enum ACCREDITATION_METHOD {
  INCOME_200K = 'income_200k',
  NET_WORTH_1M = 'net_worth_1m',
  INVESTOR_LETTER = 'investor_letter',
  NOT_ACCREDITED = 'not_accredited',
}

export enum INVESTMENT_AMOUNT {
  LESS_100K = 'less_100k',
  _100K_TO_200K = '100-200k',
  _200K_TO_300K = '200-300k',
  _300K_TO_500K = '300-500k',
  MORE_500K = 'more_500k',
}

export const ONBOARDING_STATUS_OPTIONS = [
  ONBOARDING_STATUS.ONBOARDING_STARTED,
  ONBOARDING_STATUS.ESTIMATE_VIEWED,
  ONBOARDING_STATUS.CALL_SCHEDULED,
  ONBOARDING_STATUS.CALL_ATTENDED,
  ONBOARDING_STATUS.NOT_INVESTING,
  ONBOARDING_STATUS.CONVERTED,
  ONBOARDING_STATUS.NOT_VALID,
] as const;

export const OPERATOR_SPECIALTY_OPTIONS = [
  OPERATOR_SPECIALTY.RETURNS,
  OPERATOR_SPECIALTY.DEDUCTIONS,
  OPERATOR_SPECIALTY.LIQUIDITY,
  OPERATOR_SPECIALTY.BALANCED_RETURNS_DEDUCTIONS,
  OPERATOR_SPECIALTY.LOW_RISK,
] as const;

export const FILING_STATUS_OPTIONS = [
  FILING_STATUS.SINGLE,
  FILING_STATUS.MARRIED_JOINT,
  FILING_STATUS.MARRIED_SEPARATE,
  FILING_STATUS.HEAD_HOUSEHOLD,
] as const;

export const INCOME_TYPE_OPTIONS = [
  INCOME_TYPE.W2,
  INCOME_TYPE.CONTRACT_1099,
  INCOME_TYPE.BUSINESS,
  INCOME_TYPE.OTHER,
] as const;

export const RISK_TOLERANCE_OPTIONS = [
  RISK_TOLERANCE.CONSERVATIVE,
  RISK_TOLERANCE.GROWTH_ORIENTED,
] as const;

export const ACCREDITATION_METHOD_OPTIONS = [
  ACCREDITATION_METHOD.INCOME_200K,
  ACCREDITATION_METHOD.NET_WORTH_1M,
  ACCREDITATION_METHOD.INVESTOR_LETTER,
  ACCREDITATION_METHOD.NOT_ACCREDITED,
] as const;

export const INVESTMENT_AMOUNT_OPTIONS = [
  INVESTMENT_AMOUNT.LESS_100K,
  INVESTMENT_AMOUNT._100K_TO_200K,
  INVESTMENT_AMOUNT._200K_TO_300K,
  INVESTMENT_AMOUNT._300K_TO_500K,
  INVESTMENT_AMOUNT.MORE_500K,
] as const;

export const INVESTMENT_AMOUNT_LABELS: Record<INVESTMENT_AMOUNT, string> = {
  [INVESTMENT_AMOUNT.LESS_100K]: 'Less than $100,000',
  [INVESTMENT_AMOUNT._100K_TO_200K]: '$100,000 - $200,000',
  [INVESTMENT_AMOUNT._200K_TO_300K]: '$200,000 - $300,000',
  [INVESTMENT_AMOUNT._300K_TO_500K]: '$300,000 - $500,000',
  [INVESTMENT_AMOUNT.MORE_500K]: 'More than $500,000',
} as const;

export type AccreditationMethod = `${ACCREDITATION_METHOD}`;
export type InvestmentAmount = `${INVESTMENT_AMOUNT}`;

/**
 * IMPORTANT: This list defines which fields are nested in the stepData JSON column.
 *
 * - Fields in STEP_DATA_FIELDS: Saved as { stepData: { fieldName: value } }
 * - Fields NOT in this list: Saved as direct table columns { fieldName: value }
 *
 * When adding new fields to OnboardingSessionStepData, make sure to add them here too!
 */
export const STEP_DATA_FIELDS = [
  'accreditationMethod',
  'annualIncome',
  'birthday',
  'country',
  'filingStatus',
  'incomeType',
  'interests',
  'liquidNetWorth',
  'notes',
  'pairedOperator',
  'riskPreference',
  'riskTolerance',
  'step',
  'taxState',
] as const;

export type StepDataField = (typeof STEP_DATA_FIELDS)[number];

// Helper function to check if a field should be nested in stepData
export const isStepDataField = (field: string): field is StepDataField => {
  return STEP_DATA_FIELDS.includes(field as StepDataField);
};

// Helper function to get the stepData field name from a prefixed field
export const getStepDataFieldName = (field: string): string => {
  return field.startsWith('stepData.') ? field.replace('stepData.', '') : field;
};

export interface BookingInfoCal {
  startTime: string;
  endTime?: string;
  eventTypeId?: number | null;
  eventTypeName?: string;
  bookingUid?: string;
  status?: string;
  paymentRequired?: boolean;
  isRecurring?: boolean;
  videoCallUrl?: string;
  allBookings?: unknown[];
  bookedAt: string; // When the booking was made locally
  metadata?: Record<string, unknown>;
}

export interface OnboardingSessionStepData extends Record<string, unknown> {
  step: number;
  birthday: string;
  bookingInfo?: BookingInfoCal;
  annualIncome: number;
  liquidNetWorth: number;
  country: string;
  taxState: string;
  filingStatus: FILING_STATUS;
  riskPreference: OPERATOR_SPECIALTY;
  incomeType: INCOME_TYPE | null;
  riskTolerance: RISK_TOLERANCE | null;
  pairedOperator?: string;
  accreditationMethod: AccreditationMethod | null;
  interests?: string[];
}

export interface CalendarInfo {
  type: 'cal.com' | 'calendly';
  namespace: string;
  calendarLink: string;
  originalLink: string;
}

export interface SubmitResponse {
  ok: boolean;
  submissionId: string;
  operatorId: string;
  error?: string;
}

export interface LinkedInCallbackResponse {
  ok: boolean;
  error?: string;
}
